"use client";
import { useEffect } from "react";
import store, { BeforeInstallPromptEvent } from "@/store/store";
import { mirrorPathCookie } from "@/lib/cookieMirror";

const PWA_INSTALL_NOTIFIED_KEY = "cc_pwa_install_notified";
const TENANT_PATH_REGEX = /^\/[^/]+(?:\/[^/]+)?$/;

function setPathCookie(name: string, path: string) {
    document.cookie = `${name}=${encodeURIComponent(path)}; Path=/; Max-Age=31536000; SameSite=Lax`;
    mirrorPathCookie(name, path);
}

function resolvePwaPathFromRuntime(pathname: string, departmentSlug: string): string | null {
    if (TENANT_PATH_REGEX.test(pathname)) return pathname;
    const trimmedDepartment = departmentSlug.trim();
    if (!trimmedDepartment) return null;
    return `/${trimmedDepartment}`;
}

async function armPwaInstallBonus() {
    try {
        const res = await fetch("/api/pwa-installed", {
            method: "POST",
            cache: "no-store",
        });
        if (!res.ok) return "retry";

        const json = await res.json().catch(() => null) as
            | { armed?: boolean; alreadyClaimed?: boolean }
            | null;

        if (json?.armed === true) return "armed";
        if (json?.alreadyClaimed === true) return "already_claimed";
        return "retry";
    } catch (e) {
        console.error("PWA install bonus arm failed:", e);
        return "retry";
    }
}

export default function PwaBootstrap() {
    useEffect(() => {
        const win = window as Window & { __ccDeferredPwaPrompt?: BeforeInstallPromptEvent | null };
        const syncDeferredPromptFromWindow = () => {
            if (!win.__ccDeferredPwaPrompt) return;
            store.setPwaDeferredPrompt(win.__ccDeferredPwaPrompt);
        };
        syncDeferredPromptFromWindow();

        const path = window.location.pathname;
        const resolvedLastPath = resolvePwaPathFromRuntime(path, store.department);
        if (resolvedLastPath) {
            setPathCookie("pwa_last_path", resolvedLastPath);
        }

        const isStandalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

        const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
        store.setPwaPlatform({ isStandalone, isIOS });

        if (isStandalone && localStorage.getItem(PWA_INSTALL_NOTIFIED_KEY) !== "1") {
            void armPwaInstallBonus().then((result) => {
                if (result !== "retry") {
                    localStorage.setItem(PWA_INSTALL_NOTIFIED_KEY, "1");
                }
            });
        }

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/sw2.js')
                .then(reg => console.log('SW registered:', reg))
                .catch(err => console.error('SW failed:', err));
        }

        const onAppInstalled = () => {
            const installedPath = window.location.pathname;
            const resolvedInstallPath = resolvePwaPathFromRuntime(installedPath, store.department);
            if (resolvedInstallPath) {
                setPathCookie("pwa_install_path", resolvedInstallPath);
            }

            store.setPwaDeferredPrompt(null);
            win.__ccDeferredPwaPrompt = null;
            store.close("pwaInstall");
            store.close("pwaInstallHelp");

            void armPwaInstallBonus().then((result) => {
                if (result !== "retry") {
                    localStorage.setItem(PWA_INSTALL_NOTIFIED_KEY, "1");
                }
            });
        };

        window.addEventListener("cc-beforeinstallprompt-ready", syncDeferredPromptFromWindow as EventListener);
        window.addEventListener("appinstalled", onAppInstalled);

        return () => {
            window.removeEventListener("cc-beforeinstallprompt-ready", syncDeferredPromptFromWindow as EventListener);
            window.removeEventListener("appinstalled", onAppInstalled);
        };
    }, []);

    return null;
}

